import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

/**
 * Collects an optional message when a user asks a company again to let them join (SC-301).
 *
 * This is the mirror image of AccessRequestsDeclineDialogComponent, deliberately so. That dialog lets a
 * reviewer explain a refusal; this one lets the person refused answer it. Presenting the two the same way
 * is the point: it makes the exchange read as a conversation between two people rather than a verdict
 * handed down and an identical appeal bounced back.
 *
 * Why it exists at all: without it, Request Again resent a byte-for-byte copy of the request that was just
 * turned down. The reviewer saw a second row carrying no new information, so the likely outcome was the
 * same refusal, and the user had spent one of their three attempts to learn nothing. A message is the only
 * thing that can actually change the answer.
 *
 * Optional, like the decline reason. Forcing text produces filler, and somebody who was declined purely by
 * mistake has nothing to add beyond asking again.
 */
@Component({
  selector: 'app-account-access-reason-dialog',
  templateUrl: './account-access-reason-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AccountAccessReasonDialogComponent {
  reason: string = '';

  // Matches the API's max:500 rule, so an over-long message is stopped here rather than coming back as a
  // 422 after the user has already committed one of their limited attempts.
  readonly maxLength: number = 500;

  constructor(
    public dialogRef: MatDialogRef<AccountAccessReasonDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      accountName?: string;
      declineReason?: string;
      previousReason?: string;
      attemptsRemaining?: number;
    }
  ) {}

  /**
   * Pre-fills nothing by design, but offers the previous message for reuse where there was one.
   *
   * Retyping a long message from scratch is the likely reason somebody sends a worse one the second time,
   * yet silently pre-filling it would invite sending the identical text again, which is exactly the
   * behaviour this dialog exists to break.
   */
  onReusePrevious(): void {
    this.reason = (this.data?.previousReason || '').substring(0, this.maxLength);
  }

  onConfirm(): void {
    // Closes with an object so an empty message stays distinguishable from a cancelled dialog, which
    // closes with undefined. Sending an empty reason is a real choice and must still send the request.
    this.dialogRef.close({ reason: (this.reason || '').trim() });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
